"use client";
import React, { useState, useEffect } from "react";
import { searchLeads } from "@/actions/crm/get-leads3";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { CheckCircle2, Loader2 } from "lucide-react";

interface Lead {
  id: string;
  firstName?: string;
  lastName: string;
  // Add other lead properties as needed
}

const AdvisorView: React.FC = () => {
  const { toast } = useToast();
  const [userId, setUserId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [userNotFound, setUserNotFound] = useState(false);
  const [sendingInvite, setSendingInvite] = useState<string | null>(null);
  const [invitedLeads, setInvitedLeads] = useState<Set<string>>(new Set());

  const handleSearch = async () => {
    if (!firstName || !lastName) {
      console.log("Please fill in both first name and last name");
      return;
    }
    console.log("Search button clicked");
    try {
      // Fetch leads based on the first and last name
      const { leads, userId } = await searchLeads(firstName, lastName);
      setFilteredLeads(leads);
      setUserId(userId);
      setUserNotFound(leads.length === 0); // Set userNotFound based on the number of leads
    } catch (error) {
      console.error("Error searching leads:", error);
    }
  };

  const handleSendInvite = async (leadId: string, leadName: string) => {
    setSendingInvite(leadId);
    try {
      const response = await fetch(`/api/crm/leads/${leadId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ advisedBy: userId }),
      });

      if (response.ok) {
        console.log("Lead updated successfully");
        setInvitedLeads(prev => new Set(prev).add(leadId));
        toast({
          title: "Invitation Sent Successfully!",
          description: `You are now linked as the advisor for ${leadName}. They will see you as their advisor in their profile.`,
          duration: 5000,
        });
      } else {
        console.error("Error updating lead");
        toast({
          variant: "destructive",
          title: "Failed to Send Invitation",
          description: "There was an error linking you as the advisor. Please try again.",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error("Error updating lead:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        duration: 5000,
      });
    } finally {
      setSendingInvite(null);
    }
  };
    


  useEffect(() => {
    console.log("Filtered leads:", filteredLeads);
  }, [filteredLeads]);

  const isSearchDisabled = !firstName || !lastName;

  return (
    <>
      <div className="mb-4 flex space-x-4">
        <input
          type="text"
          placeholder="First Name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="border border-gray-300 rounded-md px-4 py-2 w-1/2"
        />
        <input
          type="text"
          placeholder="Last Name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          className="border border-gray-300 rounded-md px-4 py-2 w-1/2"
        />
        <button
          onClick={handleSearch}
          className={`${
            isSearchDisabled ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500"
          } text-white rounded-md px-4 py-2`}
          disabled={isSearchDisabled}
        >
          Search
        </button>
      </div>

      {userNotFound && (
        <div className="bg-yellow-100 text-yellow-800 p-4 rounded-md mb-4">
          Investor profile not found. Please inform investor to create a profile.
        </div>
      )}


      {/* Render the filtered leads */}
      {filteredLeads.map((lead) => {
        const leadName = `${lead.firstName || ''} ${lead.lastName}`.trim();
        const isInvited = invitedLeads.has(lead.id);
        const isLoading = sendingInvite === lead.id;
        
        return (
          <Card key={lead.id} className="mb-2">
            <CardHeader>
              <CardTitle>Investor Profile</CardTitle>
              <CardDescription>Information about the investor profile</CardDescription>
            </CardHeader>
            <CardContent>
              <p>{lead.firstName}</p>
              <p>{lead.lastName}</p>
              {/* Render other lead details */}
              <div className="mt-2">
                {isInvited ? (
                  <Button variant="outline" disabled className="bg-green-50 text-green-700 border-green-300">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Linked as Advisor
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={() => handleSendInvite(lead.id, leadName)}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send invite"
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </>
  );
};

export default AdvisorView;